const key = 'streamit.account-deletion.v1';
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const token = /^[A-Za-z0-9_-]{43}$/;
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export type DeletionReceipt = { requestId: string; receipt: string; userId?: string };
const storage = () => localStorage;
function valid(value: unknown): value is DeletionReceipt {
  const v = value as DeletionReceipt | null;
  return !!v && typeof v.requestId === 'string' && uuid.test(v.requestId) && typeof v.receipt === 'string' && token.test(v.receipt)
    && (v.userId === undefined || typeof v.userId === 'string');
}
export function readDeletionReceipt(get: () => Store = storage) {
  try { const value: unknown = JSON.parse(get().getItem(key) || 'null'); if (valid(value)) return value; } catch { /* No recovered receipt. */ }
}
function recordKey(id: string) { return key + '.request.' + id; }
function userKey(id: string) { return key + '.user.' + encodeURIComponent(id); }
export function readUserDeletionReceipt(userId: string, get: () => Store = storage) {
  try {
    const value: unknown = JSON.parse(get().getItem(userKey(userId)) || 'null');
    if (valid(value) && value.userId === userId) return value;
  } catch { /* Fall back to the previous single-receipt format. */ }
  const previous = readDeletionReceipt(get);
  if (previous?.userId === userId) return previous;
}
export function saveDeletionReceipt(value: DeletionReceipt, get: () => Store = storage) {
  if (!valid(value)) throw new Error('The deletion status link could not be created.');
  try {
    const saved: unknown = JSON.parse(get().getItem(recordKey(value.requestId)) || 'null');
    if (valid(saved)) {
      if (saved.receipt !== value.receipt) throw new Error();
      // Opening a fragment must not erase the known local account association.
      value = { ...saved, ...value };
    }
    const encoded = JSON.stringify(value);
    get().setItem(recordKey(value.requestId), encoded);
    if (value.userId) get().setItem(userKey(value.userId), encoded);
    get().setItem(key, encoded);
    const restored = readDeletionReceipt(get);
    if (restored?.requestId !== value.requestId || restored.receipt !== value.receipt
      || get().getItem(recordKey(value.requestId)) !== encoded
      || (value.userId && get().getItem(userKey(value.userId)) !== encoded)) throw new Error();
  } catch { throw new Error('Allow this browser to save Streamit data before deleting your account, so you can recover its deletion status.'); }
}
export function newDeletionReceipt(userId: string): DeletionReceipt {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return { requestId: crypto.randomUUID(), receipt: btoa(String.fromCharCode(...bytes)).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', ''), userId };
}
export function deletionStatusLink(value: DeletionReceipt, origin = location.origin) {
  if (!valid(value)) throw new Error('Invalid deletion status link.');
  return `${origin}/account-deletion#request=${value.requestId}&receipt=${value.receipt}`;
}
export function captureDeletionReceipt(href: string, get: () => Store = storage): { captured: boolean; value?: DeletionReceipt } {
  const url = new URL(href);
  if (url.pathname !== '/account-deletion' || (!url.hash && !url.search)) return { captured: false };
  const params = new URLSearchParams(url.hash.slice(1));
  const value = { requestId: params.get('request'), receipt: params.get('receipt') };
  // Query tokens/duplicates/extra fields are never adopted. A malformed link
  // must not overwrite the previous usable recovery receipt in local storage.
  if (url.search || [...params].length !== 2 || params.getAll('request').length !== 1 || params.getAll('receipt').length !== 1 || !valid(value)) return { captured: true };
  try { saveDeletionReceipt(value, get); } catch { /* A supplied link still works in memory when storage is blocked. */ }
  return { captured: true, value };
}
export function clearDeletionReceipt(get: () => Store = storage, value = readDeletionReceipt(get)) {
  if (!value) return;
  try {
    const saved: unknown = JSON.parse(get().getItem(recordKey(value.requestId)) || 'null');
    if (valid(saved) && saved.receipt === value.receipt) {
      get().removeItem(recordKey(value.requestId));
      if (saved.userId && readUserDeletionReceipt(saved.userId, get)?.requestId === value.requestId) get().removeItem(userKey(saved.userId));
    }
    // Another tab may have saved a different receipt while this page was open.
    const latest = readDeletionReceipt(get);
    if (latest?.requestId === value.requestId && latest.receipt === value.receipt) get().removeItem(key);
  } catch { /* Already unavailable. */ }
}
export type DeletionStatus = { requestId: string; phase: 'pending' | 'household_recovery' | 'complete'; connectionsClosed: boolean; canRetry: boolean; retryAt: number | null; error: string | null; cloudFilesDeleted: false };
export async function receiptStatus(value: DeletionReceipt, retry = false, signal?: AbortSignal): Promise<DeletionStatus> {
  // This minimal endpoint deliberately works after Clerk sign-out/deletion and
  // does not call the human token provider or the native authenticated bridge.
  const response = await fetch(`/api/v1/account/deletion/${retry ? 'retry' : 'status'}`, {
    method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer', signal,
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ requestId: value.requestId, receipt: value.receipt }),
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.error?.message || 'Deletion status is temporarily unavailable. Keep this link and try again.');
  if (body?.requestId !== value.requestId || !['pending', 'household_recovery', 'complete'].includes(body?.phase) || body.cloudFilesDeleted !== false)
    throw new Error('Deletion status could not be confirmed. Keep this link and try again.');
  return body as DeletionStatus;
}
